import { Component, OnInit } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Store } from '@ngrx/store';
import { ESkeletonType, INSGridConfig } from '@norfolk-southern/accessns-components';
import { ColDef, GridOptions } from 'ag-grid-community';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { NSCommonConstants } from '@ruby/configs/common.constants';
import { NSECRConstants } from '@ruby/configs/ecr.constants';
import { ECRMetricsActions, ECRMetricsSelectors, fromECRMetrics } from '@ruby/modules/ecr/store/ecr-metrics.index';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { qualtrics } from '@ruby/modules/ecr/submodules/ecr-metrics/containers/survey-lib-qualtrics.js';
import { IECRPattern } from '@ruby/shared/models/ecr/ecr.interface';

@Component({
  selector: 'ng-ecr-metrics',
  templateUrl: './ecr-metrics.container.html',
  styleUrls: ['./ecr-metrics.container.scss']
})
export class ECRMetricsContainer implements OnInit {
  columns: Array<ColDef> = NSECRConstants.metricsColumns;
  store$: Observable<{ state: fromECRMetrics.State; pattern?: IECRPattern }>;
  pattern?: IECRPattern;
  skeleton: ESkeletonType = ESkeletonType.WIDGET;
  chartConfig!: Record<string, unknown>;
  gridOptions: GridOptions = NSECRConstants.metricsGridOptions;
  gridConfig: INSGridConfig = NSECRConstants.gridConfig;
  private index = 0;

  constructor(private store: Store<{ state: fromECRMetrics.State; pattern?: IECRPattern }>) {
    this.store$ = this.store.select(ECRMetricsSelectors.selectMetrics).pipe(tap((st: {
      state: fromECRMetrics.State; pattern?: IECRPattern;
    }) => {
      const maxValue: number = Math.max(
        ...st.state.data.accepted,
        ...st.state.data.totalAccepted,
        ...st.state.data.orderFills,
        ...st.state.data.resetCounts,
        ...st.state.data.loadFills,
        ...st.state.data.badOrders,
        ...st.state.data.rejectedEvents,
        ...st.state.data.releasedEmpties
      );
      this.chartConfig = {
        title: {
          show: maxValue === 0,
          textStyle: {
            color: 'grey',
            fontSize: 14
          },
          text: NSCommonConstants.notFoundMessage,
          left: 'center',
          top: 'center'
        },
        tooltip: NSECRConstants.comboChartConfig.tooltip,
        color: NSECRConstants.comboChartConfig.color,
        toolbox: NSECRConstants.comboChartConfig.toolbox,
        legend: NSECRConstants.comboChartConfig.legend,
        xAxis: [{ ...NSECRConstants.comboChartConfig.xAxis[0], data: st.state.data.eventDates }],
        yAxis: [{
          ...NSECRConstants.comboChartConfig.yAxis[0],
          max: maxValue
        }, NSECRConstants.comboChartConfig.yAxis[1]],
        series: [
          { ...NSECRConstants.comboChartConfig.series[0], data: st.state.data.accepted },
          { ...NSECRConstants.comboChartConfig.series[1], data: st.state.data.totalAccepted },
          { ...NSECRConstants.comboChartConfig.series[2], data: st.state.data.orderFills },
          { ...NSECRConstants.comboChartConfig.series[3], data: st.state.data.resetCounts },
          { ...NSECRConstants.comboChartConfig.series[4], data: st.state.data.loadFills },
          { ...NSECRConstants.comboChartConfig.series[5], data: st.state.data.badOrders },
          { ...NSECRConstants.comboChartConfig.series[6], data: st.state.data.rejectedEvents },
          { ...NSECRConstants.comboChartConfig.series[7], data: st.state.data.releasedEmpties }
        ]
      };

      if (st.pattern && st.pattern !== this.pattern) {
        this.pattern = st.pattern;
        this.dispatchAction();
      }
    }));
  }

  ngOnInit(): void {
    this.loadQSISurvey();
  }

  /**
   * Update Index
   *
   * @summary updates the internal index reference
   * @param $event: MatTabChangeEvent
   * @returns void
   */
  updateIndex($event: MatTabChangeEvent): void {
    if (this.index !== $event.index) {
      this.index = $event.index;

      this.dispatchAction();
    }
  }

  /**
   * Dispatch Actions
   *
   * @summary Depending on the index it dispatches load metrics/grid
   * @returns void
   */
  dispatchAction(): void {
    if (this.index === 0) {
      this.store.dispatch(ECRMetricsActions.loadMetrics({ data: this.pattern! }));
    } else {
      this.store.dispatch(ECRMetricsActions.loadGrid({ data: this.pattern! }));
    }
  }

  private loadQSISurvey(): void {
    qualtrics.start();
  }
}
